/**
 * GCS関連のサービス関数
 */
import { apiRequest, parseApiResponse } from '../utils/api';

/**
 * 署名付きURLを更新
 */
export const refreshPdfUrl = async (
  sessionId: string,
  currentUrl?: string,
): Promise<{ pdf_url: string; expires_in_hours: number }> => {
  try {
    // current_urlをクエリパラメータとして追加（古いプロジェクト対応）
    let endpoint = `/api/refresh-pdf-url/${sessionId}`;
    if (currentUrl) {
      endpoint += `?current_url=${encodeURIComponent(currentUrl)}`;
    }

    const response = await apiRequest(endpoint);

    if (!response.ok) {
      throw new Error(`URL更新失敗: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ PDF URL更新成功:', data.message);

    return {
      pdf_url: data.pdf_url,
      expires_in_hours: data.expires_in_hours,
    };
  } catch (error) {
    console.error('❌ PDF URL更新エラー:', error);
    throw error;
  }
};

/**
 * URLが期限切れかどうかを検証
 * @returns true: 期限切れ, false: 有効, null: 判定不可能（CORS エラー等）
 */
export const isPdfUrlExpired = async (url: string): Promise<boolean | null> => {
  try {
    // HEADリクエストでURLの有効性をチェック
    // GCSへの直接リクエストなので auth token は不要
    const response = await fetch(url, {
      method: 'HEAD',
      cache: 'no-cache',
    });

    if (response.ok) {
      console.log('✅ PDF URLは有効です (Status: 200)');
      return false;
    } else {
      console.log(`⚠️ PDF URLが無効です (Status: ${response.status})`);
      return true;
    }
  } catch (error) {
    console.error('❌ PDF URL検証エラー (CORS問題の可能性):', error);
    // CORSエラーの場合、判定不可能なので null を返す
    return null;
  }
};

/**
 * PDFプレビュー用のURL取得（自動更新付き）
 */
export const getValidPdfUrl = async (sessionId: string, currentUrl: string): Promise<string> => {
  try {
    // 現在のURLが有効かチェック
    const isExpired = await isPdfUrlExpired(currentUrl);

    // 判定不可能な場合（CORSエラー）は安全のため新しいURLを取得
    if (isExpired === null) {
      console.log('⚠️ URL検証ができないため、安全のため新しいURLを取得します');
      try {
        const refreshed = await refreshPdfUrl(sessionId, currentUrl);
        console.log('✅ PDF URL強制更新成功');
        return refreshed.pdf_url;
      } catch (refreshError) {
        console.error('❌ PDF URL強制更新失敗、現在のURLを使用:', refreshError);
        return currentUrl;
      }
    }

    if (!isExpired) {
      console.log('✅ 現在のURLは有効です');
      return currentUrl;
    }

    // 期限切れの場合は新しいURLを取得
    console.log('🔄 PDF URLが期限切れです。更新中...');
    const refreshed = await refreshPdfUrl(sessionId, currentUrl);

    return refreshed.pdf_url;
  } catch (error) {
    console.error('PDF URL取得エラー:', error);
    throw new Error('PDFの表示に失敗しました。');
  }
};

/**
 * 動画の署名付きURLを更新
 */
export const refreshVideoUrl = async (
  itemId: string,
  blobName: string,
): Promise<{ file_url: string; expires_in_hours: number }> => {
  try {
    const endpoint = `/api/video/refresh-url/${itemId}?blob_name=${encodeURIComponent(blobName)}`;

    const response = await apiRequest(endpoint);

    if (!response.ok) {
      throw new Error(`動画URL更新失敗: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ 動画URL更新成功:', data);

    return {
      file_url: data.file_url,
      expires_in_hours: data.expires_in_hours,
    };
  } catch (error) {
    console.error('❌ 動画URL更新エラー:', error);
    throw error;
  }
};

/**
 * 動画プレビュー用のURL取得（自動更新付き）
 */
export const getValidVideoUrl = async (
  itemId: string,
  currentUrl: string,
  blobName: string,
): Promise<string> => {
  try {
    // 現在のURLが空または期限切れかチェック
    if (!currentUrl || currentUrl.trim() === '') {
      console.log('🔄 動画URLが空です。新しいURLを取得中...');
      const refreshed = await refreshVideoUrl(itemId, blobName);
      return refreshed.file_url;
    }

    // 現在のURLが有効かチェック
    const isExpired = await isPdfUrlExpired(currentUrl);

    // 判定不可能な場合（CORSエラー）は安全のため新しいURLを取得
    if (isExpired === null) {
      console.log('⚠️ 動画URL検証ができないため、安全のため新しいURLを取得します');
      try {
        const refreshed = await refreshVideoUrl(itemId, blobName);
        console.log('✅ 動画URL強制更新成功');
        return refreshed.file_url;
      } catch (refreshError) {
        console.error('❌ 動画URL強制更新失敗、現在のURLを使用:', refreshError);
        return currentUrl;
      }
    }

    if (!isExpired) {
      console.log('✅ 現在の動画URLは有効です');
      return currentUrl;
    }

    // 期限切れの場合は新しいURLを取得
    console.log('🔄 動画URLが期限切れです。更新中...');
    const refreshed = await refreshVideoUrl(itemId, blobName);
    return refreshed.file_url;
  } catch (error) {
    console.error('動画URL取得エラー:', error);
    throw new Error('動画の表示に失敗しました。');
  }
};

/**
 * 補正済み文字起こしを取得
 */
export const fetchRefinedTranscript = async (
  itemId: string,
): Promise<{
  item_id: string;
  segments: Array<{
    segment_id: string;
    start_sec: number;
    end_sec: number;
    original_text: string;
    refined_text: string;
  }>;
  total_duration_sec: number;
  created_at: string;
} | null> => {
  try {
    const endpoint = `/api/video/refined-transcript/${itemId}`;
    const response = await apiRequest(endpoint);

    if (response.status === 404) {
      console.log('📝 補正済み文字起こしがまだ生成されていません');
      return null;
    }

    if (!response.ok) {
      throw new Error(`補正済み文字起こし取得失敗: ${response.status}`);
    }

    const data = await parseApiResponse<any>(response);
    console.log('✅ 補正済み文字起こし取得成功:', data?.segments?.length, 'セグメント');
    return data;
  } catch (error) {
    console.error('❌ 補正済み文字起こし取得エラー:', error);
    return null;
  }
};
